import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
  Avatar,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { createPost, generateAIContent } from "../api";
import { rewriteToCloudFront } from "../images";

function CreatePost({ onPostCreated }) {
  const [text, setText] = useState("");
  const [image, setImage] = useState(null);
  const [useImage, setUseImage] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    const newPost = await createPost(text, image);
    onPostCreated(newPost);
    setText("");
    setImage(null);
    setUseImage(false);
  };

  const handleGenerateAI = async () => {
    setIsGenerating(true);
    try {
      const imagePrompt = useImage ? text : null;
      const { generatedText, imageUrl } = await generateAIContent(
        text,
        imagePrompt
      );
      setText(generatedText);
      setImage(imageUrl);
    } catch (error) {
      console.error("Failed to generate AI content:", error.message);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Box
      sx={{
        borderBottom: 1,
        borderColor: "divider",
        backgroundColor: "transparent",
        width: "100%",
      }}
    >
      <Box sx={{ display: "flex", gap: 1.5, p: 2, width: "100%" }}>
        <Avatar
          sx={{
            width: 40,
            height: 40,
          }}
        >
          A
        </Avatar>
        <Box sx={{ flex: 1, width: "100%" }}>
          <TextField
            fullWidth
            multiline
            rows={3}
            variant="standard"
            placeholder="What's happening?"
            value={text}
            onChange={(e) => setText(e.target.value)}
            disabled={isGenerating}
            InputProps={{
              disableUnderline: true,
            }}
            sx={{
              mb: 2,
              width: "100%",
              "& .MuiInputBase-root": {
                fontSize: "1.25rem",
                color: "text.primary",
                lineHeight: 1.5,
                width: "100%",
              },
              "& .MuiInputBase-input": {
                p: 0,
                width: "100%",
              },
              "& .MuiInputBase-input::placeholder": {
                color: "text.secondary",
                opacity: 1,
              },
            }}
          />

          {image && (
            <Box
              sx={{
                mb: 2,
                borderRadius: 2,
                overflow: "hidden",
                border: 1,
                borderColor: "divider",
                position: "relative",
              }}
            >
              <Box
                component="img"
                src={rewriteToCloudFront(image)}
                alt="Post media"
                sx={{
                  width: "100%",
                  height: "auto",
                  display: "block",
                }}
              />
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderTop: 1,
              borderColor: "divider",
              pt: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={useImage}
                    onChange={(e) => setUseImage(e.target.checked)}
                    size="small"
                    sx={{
                      p: 0.5,
                      color: "primary.main",
                      "&.Mui-checked": {
                        color: "primary.main",
                      },
                    }}
                  />
                }
                label="Image"
                sx={{
                  mr: 1,
                  color: "text.secondary",
                  "& .MuiFormControlLabel-label": {
                    fontSize: "0.875rem",
                  },
                }}
              />
              <Button
                onClick={handleGenerateAI}
                disabled={isGenerating}
                variant="text"
                sx={{
                  minWidth: 40,
                  height: 32,
                  p: 0.5,
                  color: "primary.main",
                  "&:hover": {
                    backgroundColor: "rgba(29, 155, 240, 0.1)",
                  },
                }}
              >
                {isGenerating ? (
                  <CircularProgress size={16} thickness={4} />
                ) : (
                  "AI"
                )}
              </Button>
            </Box>

            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={!text.trim() || isGenerating}
              sx={{
                minWidth: 64,
                height: 32,
                px: 2,
                backgroundColor: "primary.main",
                "&:disabled": {
                  backgroundColor: "primary.main",
                  opacity: 0.5,
                },
              }}
            >
              Post
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default CreatePost;
